import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Grid,
  Typography,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
//date range picker
import dayjs from "dayjs";
import "dayjs/locale/zh-tw";

//mui-icons
import CloseIcon from "@mui/icons-material/Close";

//狀態管理
import { observer } from "mobx-react";
import emissionViewStore from "../../../Store/marco/emissionViewStore.js";
import { dataEntryAPI } from "../../API/dataEntryAPI.js";
import { getConstByStandard } from "../../utils/getConstFileByStandard.js";
//翻譯
import { useTranslation } from "react-i18next";
//hooks
import { CustomSelector } from "../CustomSelector.js";
import { CustomTextField } from "../CustomTextField.js";
import { CustomDatePicker } from "../CustomDatePicker.js";
import { DateRangePicker } from "../DateRangePicker.js";
import {
  DATA_ENTRY_CALCULATION_IDS,
  formatWeightDistance,
  hasNumericValue,
  isValidNonNegativeNumber,
} from "../../utils/dataEntryCalculation.js";

// 之後新增 cat，只要在這裡加新欄位即可
const INITIAL_CAT_FIELDS = {
  // cat = 3_1 / 3_2
  weight: "",
  distance: "",
  // cat = 3_3,3_4,3_5
  origin: "",
  destination: "",
  tripTypeValue: 1, //單程:1來回:2
  distance_3_3: "",
  passengers: 1,
  numberOfTrips: 1,
};

const INITIAL_ERRORS = {
  unit: false,
  dataFormat: false,
  usedDate: false,
  consumptionValue: false,
  dateRange: false,
  weight: false,
  distance: false,
  distance_3_3: false,
  passengers: false,
  numberOfTrips: false,
  tripTypeValue: false,
};

//main
const DataEntryAddAndSetUnitModal = observer(() => {
  const store = emissionViewStore;
  const CONST = getConstByStandard(store.standardId);
  const { t } = useTranslation("ActivityMain");

  //set unit
  const [unit, setUnit] = useState("");
  const [dataFormat, setDataFormat] = useState("");
  const [unitOptions, setUnitOptions] = useState([]);
  const [usedDate, setUsedDate] = useState(null); //Used Date
  const [consumptionValue, setConsumptionValue] = useState(""); //Consumption Value /Quantity
  const [confirmedRange, setConfirmedRange] = useState(null); //日期區間
  //檢查按鈕是否可以點擊
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  //提示使用者當月已有資料的錯誤訊息是否要顯示
  const [isHelpText, setIsHelpText] = useState(false);
  //取得數據輸入單位提示與說明
  const [hints, setHints] = useState({
    activity: null,
    land: null,
    sea: null,
    air: null,
  });
  //新增的欄位
  const [catFields, setCatFields] = useState(INITIAL_CAT_FIELDS);
  //儲存錯誤訊息
  const [errors, setErrors] = useState(INITIAL_ERRORS);

  // 單一欄位 setter，避免每次都展開整個物件
  const setCatField = (field, value) =>
    setCatFields((prev) => ({ ...prev, [field]: value }));

  const clearError = (field) =>
    setErrors((prev) => ({ ...prev, [field]: false }));

  const calcCat3xConsumption = (overrides = {}) => {
    const fields = { ...catFields, ...overrides };
    const base =
      fields.distance_3_3 * fields.numberOfTrips * fields.tripTypeValue;
    return emissionViewStore.selectedDataEntry.calculationId === 4
      ? base * fields.passengers
      : base;
  };

  // ─── 衍生值 ──────────────────────────────────────────────
  const category = CONST.category;
  const scope = category[emissionViewStore.selectedTab].split("_")[0];
  const cat = category[emissionViewStore.selectedTab].substring(3);
  // cat = 3_3|| 3_4 || 3_5專屬 (ghg cat3.6& 3.7)
  const isCat3basic = ["3_1", "3_2"].includes(cat);
  const isCat3x = ["3_3", "3_4", "3_5"].includes(cat);
  const calculationId =
    emissionViewStore.selectedDataEntry?.calculationId;
  const isWeightDistanceBased =
    isCat3basic &&
    calculationId === DATA_ENTRY_CALCULATION_IDS.WEIGHT_DISTANCE;
  const isDistanceBased =
    isCat3basic && calculationId === DATA_ENTRY_CALCULATION_IDS.DISTANCE;
  const isFuelBased =
    calculationId === DATA_ENTRY_CALCULATION_IDS.FUEL;
  const unitName =
    unitOptions.find((option) => option.unitId === unit)?.unitName ?? "";
  //passengers-km get km
  const extractedUnit =
    (unit === 17 || unit === 18) && unitName.includes("-")
      ? unitName.split("-").pop()
      : unitName;

  //tonne-km get tonne and km
  const [weightUnit, distanceUnit] = isCat3basic
    ? (unitName ?? "").split("-")
    : [];

  const getHints = async () => {
    try {
      // 定義要處理的資料 ID 與對應的 key
      const dataMap = [
        {
          id: emissionViewStore.selectedDataEntry.activityId,
          key: "activity",
        },
        {
          id: emissionViewStore.selectedDataEntry.activityLandId,
          key: "land",
        },
        {
          id: emissionViewStore.selectedDataEntry.activitySeaId,
          key: "sea",
        },
        {
          id: emissionViewStore.selectedDataEntry.activityAirId,
          key: "air",
        },
      ];

      // 建立新的 hints 物件
      const newHints = { ...hints };

      // 使用 Promise.all 同時處理所有有效的 ID
      await Promise.all(
        dataMap
          .filter((item) => item.id) // 過濾掉 ID 為 0 或 undefined 的項目
          .map(async ({ id, key }) => {
            try {
              const data = await dataEntryAPI.DataEntryHintAPI(id, scope);
              console.log(`取得 ${key} 數據輸入單位提示與說明:`, data);
              newHints[key] = data;
            } catch (error) {
              console.error(
                `Error fetching data for ${key} (ID: ${id}):`,
                error,
              );
              newHints[key] = null;
            }
          }),
      );

      // 一次性更新所有 hints
      setHints(newHints);
    } catch (err) {
      console.error("Error in getHints function:", err);
    }
  };

  //取得單位下拉式清單資料
  const getOptions = async () => {
    try {
      const data = await dataEntryAPI.unitOptionDataEntryAPI(
        emissionViewStore.selectedDataEntry.id,
        scope,
        cat,
      ); // 使用引入的 API 函數
      console.log("取得單位下拉式清單資料:", data);
      setUnitOptions(data);
    } catch (err) {
      console.error("Error fetching data:", err);
    }
  };

  //reset
  const resetForm = () => {
    setUsedDate(null);
    setConsumptionValue("");
    setConfirmedRange(null);
    setCatFields(INITIAL_CAT_FIELDS); // ← 一行搞定所有 cat 專屬欄位
    setErrors(INITIAL_ERRORS);
    setIsHelpText(false);
  };

  //驗證
  const buildValidationErrors = () => {
    const calcId = calculationId;
    const isCat3xCalc2or4 = isCat3x && (calcId === 2 || calcId === 4);
    const requiresManualConsumption =
      isFuelBased ||
      !(isWeightDistanceBased || isDistanceBased || isCat3x);

    return {
      unit: !unit,
      dataFormat: !dataFormat,
      usedDate: dataFormat !== 4 && !usedDate,
      dateRange:
        dataFormat === 4 &&
        (!confirmedRange?.startDate || !confirmedRange?.endDate),

      consumptionValue:
        requiresManualConsumption &&
        !isValidNonNegativeNumber(consumptionValue),

      // cat = 3_1 / 3_2 專屬
      weight:
        isWeightDistanceBased &&
        !isValidNonNegativeNumber(catFields.weight),
      distance:
        (isWeightDistanceBased || isDistanceBased) &&
        !isValidNonNegativeNumber(catFields.distance),

      // cat = 3_3 / 3_4 / 3_5，calculationId === 2 or 4
      tripTypeValue: isCat3xCalc2or4 && !catFields.tripTypeValue,
      numberOfTrips:
        isCat3xCalc2or4 &&
        (!catFields.numberOfTrips || Number(catFields.numberOfTrips) <= 0),
      distance_3_3:
        isCat3xCalc2or4 &&
        (catFields.distance_3_3 === "" || Number(catFields.distance_3_3) < 0),
      passengers:
        isCat3x &&
        calcId === 4 &&
        (!catFields.passengers || Number(catFields.passengers) <= 0),
    };
  };

  //存檔
  const handleSave = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const newErrors = buildValidationErrors();
      setErrors(newErrors);
      if (Object.values(newErrors).some(Boolean)) return;

      if (dataFormat === 1) {
        const currentDevice =
          emissionViewStore.dataEntryListData?.fullList.find(
            (item) => item.id === emissionViewStore.selectedDataEntry.id,
          );
        if (currentDevice && isMonthDataExists(currentDevice, usedDate)) {
          setIsHelpText(true);
          setIsDisabled(true);
          return;
        }
      }

      const settingSuccess = await emissionViewStore.setDE(dataFormat, unit, {
        showSuccess: false,
      });
      if (!settingSuccess) return;

      const addSuccess = await emissionViewStore.addDE(
        dataFormat,
        usedDate,
        confirmedRange,
        consumptionValue,
        catFields,
        isCat3x,
        isCat3basic,
      );
      if (!addSuccess) return;

      await emissionViewStore.getDEListData({ keepPage: true });
      emissionViewStore._handleSetIsShowDataEntryAddAndSetUnitModal(false);
    } catch (error) {
      console.error("Error in save operation", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const timeFormatOptions = [
    { value: t("Year"), dataTypeId: 2 },
    { value: t("Month"), dataTypeId: 1 },
    { value: t("Day"), dataTypeId: 3 },
    { value: t("Interval"), dataTypeId: 4 },
  ];

  useEffect(() => {
    if (emissionViewStore.isShowDataEntryAddAndSetUnitModal) {
      getOptions();
      getHints();
      setDataFormat(emissionViewStore.selectedDataEntry.dataTypeId || "");
      setUnit(emissionViewStore.selectedDataEntry.dataUnitId || "");
      setUsedDate(
        emissionViewStore.selectedDataEntry.dataTypeId === 2
          ? dayjs().year(emissionViewStore.selectedYear)
          : null,
      );
      // catFields 與 errors 由 resetForm 處理
      const selectedEntry = emissionViewStore.selectedDataEntry;
      const isStaticWeightDistance =
        isWeightDistanceBased && selectedEntry?.isStatic;
      const initialFields = {
        ...INITIAL_CAT_FIELDS,
        ...(isStaticWeightDistance && {
          origin: selectedEntry.origin ?? "",
          destination: selectedEntry.destination ?? "",
          distance: selectedEntry.distance ?? "",
        }),
      };
      setCatFields(initialFields);
      setConsumptionValue(
        isWeightDistanceBased
          ? formatWeightDistance(
              initialFields.weight,
              initialFields.distance,
            )
          : isDistanceBased
            ? initialFields.distance
            : "",
      );
      setErrors(INITIAL_ERRORS);
    } else {
      resetForm();
      setUnit("");
      setDataFormat("");
      setHints({ activity: null, land: null, sea: null, air: null });
    }
  }, [emissionViewStore.isShowDataEntryAddAndSetUnitModal]);

  useEffect(() => {
    if (dataFormat === 2) {
      setUsedDate(dayjs().year(emissionViewStore.selectedYear));
    }
  }, [dataFormat]);

  if (!emissionViewStore.selectedDataEntry) return null;

  //////// 月份重複檢查
  const isMonthDataExists = (deviceData, selectedDate) => {
    if (!deviceData || !deviceData.data || !Array.isArray(deviceData.data)) {
      console.log("設備數據結構不正確");
      return false;
    }

    // 確保 selectedDate 是有效的 dayjs 對象並格式化
    const formattedMonth = dayjs(selectedDate).format("YYYY-MM");

    // 遍歷所有數據項
    return deviceData.data.some((timeData) => {
      if (!timeData.datas || !Array.isArray(timeData.datas)) {
        return false;
      }

      // 檢查該時間段的所有數據項
      return timeData.datas.some((data) => {
        const dataMonth = dayjs(data.usedDate).format("YYYY-MM");
        console.log("比較日期:", dataMonth, "vs", formattedMonth);
        return dataMonth === formattedMonth;
      });
    });
  };

  const handleDateChange = (selectedDate) => {
    if (!selectedDate) return;
    if (dataFormat === 1) {
      const currentDevice = emissionViewStore.dataEntryListData?.fullList.find(
        (item) => item.id === emissionViewStore.selectedDataEntry.id,
      );
      if (currentDevice && isMonthDataExists(currentDevice, selectedDate)) {
        setIsHelpText(true);
        setIsDisabled(true);
        return;
      }
    }
    setUsedDate(selectedDate);
    setIsHelpText(false);
    setIsDisabled(false);
    clearError("usedDate");
  };

  /////////////////////////////////////////////
  return (
    <Dialog
      open={emissionViewStore.isShowDataEntryAddAndSetUnitModal}
      onClose={() =>
        emissionViewStore._handleSetIsShowDataEntryAddAndSetUnitModal(false)
      }
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        <IconButton
          onClick={() =>
            emissionViewStore._handleSetIsShowDataEntryAddAndSetUnitModal(false)
          }
          sx={{
            position: "absolute",
            right: 8,
            top: 10,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
        {`${t("Set and Add Data for")} ${
          emissionViewStore.selectedDataEntry.itemName
        }`}
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Grid
          container
          spacing={2}
          justifyContent="flex-start"
          alignItems="flex-start"
          rowSpacing={1}
        >
          {/* 使用單位選項 */}
          <Grid size={6}>
            <CustomSelector
              label={t("Choose Unit")}
              value={unit} // 這裡的 unit 現在應該存儲 unitId
              handleChange={(e, selectedOption) => {
                setUnit(selectedOption.unitId); // 存儲 ID 而非文字
                clearError("unit");
              }}
              options={unitOptions}
              valueKey="unitId"
              labelKey="unitName"
              error={errors.unit}
              placeholder={t("Unset")}
              disabled={store.selectedDataEntry.hasDirectApiEntryEntity}
            />
          </Grid>
          {/* 使用時間格式選項 */}
          <Grid size={6}>
            <CustomSelector
              label={t("Choose Time Format")}
              value={dataFormat} // 這裡的 format 現在應該存儲 dataTypeId
              handleChange={(e, selectedOption) => {
                setDataFormat(selectedOption.dataTypeId); // 存儲 ID 而非文字
                clearError("dataFormat");
              }}
              options={timeFormatOptions}
              valueKey="dataTypeId"
              labelKey="value"
              error={errors.dataFormat}
              disabled={
                !emissionViewStore.selectedDataEntry.allowModifyUnit ||
                store.selectedDataEntry.hasDirectApiEntryEntity
              }
              showTooltip={true} // 加這行
            />
          </Grid>

          {/* cat = 3_1/3_2/3_3/3_4/3_5：出發地 / 目的地 (非必填) */}
          {(isCat3x || isCat3basic) && (
            <>
              <Grid size={6}>
                <CustomTextField
                  label={t("Origin")}
                  placeholder="Origin"
                  value={catFields.origin}
                  type="text"
                  handleChange={(e) => setCatField("origin", e.target.value)}
                  disabled={
                    isWeightDistanceBased &&
                    emissionViewStore.selectedDataEntry?.isStatic
                  }
                />
              </Grid>
              <Grid size={6}>
                <CustomTextField
                  label={t("Destination")}
                  placeholder="Destination"
                  type="text"
                  value={catFields.destination}
                  handleChange={(e) =>
                    setCatField("destination", e.target.value)
                  }
                  disabled={
                    isWeightDistanceBased &&
                    emissionViewStore.selectedDataEntry?.isStatic
                  }
                />
              </Grid>
            </>
          )}

          {/* cat = 3_3/3_4/3_5 基於乘客距離，calculationId === 4 */}
          {isCat3x &&
            emissionViewStore.selectedDataEntry.calculationId !== 3 && (
              <>
                <Grid
                  size={12}
                  sx={{ backgroundColor: "#f2f2f2", p: 2, borderRadius: "4px" }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography variant="subtitle1">
                      <span style={{ color: "red" }}>*</span> {t("Trip Type")}
                    </Typography>
                    <RadioGroup
                      row
                      value={catFields.tripTypeValue}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        setCatField("tripTypeValue", v);
                        setConsumptionValue(
                          calcCat3xConsumption({ tripTypeValue: v }),
                        );
                      }}
                    >
                      <FormControlLabel
                        value={1}
                        control={<Radio />}
                        label={t("One Way")}
                      />
                      <FormControlLabel
                        value={2}
                        control={<Radio />}
                        label={t("Round Trip")}
                      />
                    </RadioGroup>
                  </Box>
                </Grid>
                {/* 行程次數 */}
                <Grid size={6}>
                  <CustomTextField
                    required
                    label={t("Number of Trips")}
                    type="number"
                    unit="times"
                    min={1}
                    placeholder="Enter Number of Trips"
                    value={catFields.numberOfTrips}
                    handleChange={(e) => {
                      const v = e.target.value;
                      setCatField("numberOfTrips", v);
                      clearError("numberOfTrips");
                      setConsumptionValue(
                        calcCat3xConsumption({ numberOfTrips: v }),
                      );
                    }}
                    error={errors.numberOfTrips}
                  />
                </Grid>
                {/* 距離 */}
                <Grid size={6}>
                  <CustomTextField
                    required
                    label={t("Distance")}
                    type="number"
                    unit={extractedUnit}
                    value={catFields.distance_3_3}
                    placeholder="Enter Distance"
                    handleChange={(e) => {
                      const v = e.target.value;
                      setCatField("distance_3_3", v);
                      clearError("distance_3_3");
                      setConsumptionValue(
                        calcCat3xConsumption({ distance_3_3: v }),
                      );
                    }}
                    error={errors.distance_3_3}
                  />
                </Grid>
                {/* 乘客人數 */}
                {emissionViewStore.selectedDataEntry.calculationId === 4 && (
                  <Grid size={6}>
                    <CustomTextField
                      required
                      label={t("Number of Passengers")}
                      type="number"
                      unit="pax"
                      min={1}
                      placeholder="Enter Passenger"
                      value={catFields.passengers}
                      handleChange={(e) => {
                        const v = e.target.value;
                        setCatField("passengers", v);
                        clearError("passengers");
                        setConsumptionValue(
                          calcCat3xConsumption({ passengers: v }),
                        );
                      }}
                      error={errors.passengers}
                    />
                  </Grid>
                )}
              </>
            )}
          {(isWeightDistanceBased || isDistanceBased) && (
            <>
              {isWeightDistanceBased && (
                <Grid size={6}>
                  <CustomTextField
                    required
                    label={t("Weight")}
                    value={catFields.weight}
                    unit={t(weightUnit) || ""}
                    handleChange={(e) => {
                      const v = e.target.value;
                      setCatField("weight", v);
                      clearError("weight");
                      setConsumptionValue(
                        formatWeightDistance(v, catFields.distance),
                      );
                    }}
                    error={errors.weight}
                  />
                </Grid>
              )}
              <Grid size={6}>
                <CustomTextField
                  required
                  label={t("Distance")}
                  unit={
                    isWeightDistanceBased
                      ? t(distanceUnit) || ""
                      : t(unitName) || ""
                  }
                  value={catFields.distance}
                  handleChange={(e) => {
                    const v = e.target.value;
                    setCatField("distance", v);
                    clearError("distance");
                    setConsumptionValue(
                      isWeightDistanceBased
                        ? formatWeightDistance(catFields.weight, v)
                        : v,
                    );
                  }}
                  error={errors.distance}
                  disabled={
                    isWeightDistanceBased &&
                    emissionViewStore.selectedDataEntry?.isStatic
                  }
                />
              </Grid>
            </>
          )}
          {/* 數據時間 */}
          <Grid size={6}>
            {/* <Grid size={{ xs: 12, sm: 6 }}> */}
            {dataFormat === 4 ? (
              <DateRangePicker
                confirmedRange={confirmedRange}
                setConfirmedRange={setConfirmedRange}
                error={errors.dateRange}
                clearDateRangeError={() => clearError("dateRange")}
                year={emissionViewStore.selectedYear}
              />
            ) : (
              <CustomDatePicker
                label={t("Used Date")}
                value={usedDate}
                handleChange={handleDateChange}
                error={errors.usedDate}
                dataFormat={dataFormat}
              />
            )}
          </Grid>
          <Grid
            size={
              emissionViewStore.selectedDataEntry.calculationId === 4 ? 12 : 6
            }
          >
            {/* 活動數據 */}
            {isFuelBased ? (
              <CustomTextField
                required
                label={t("Fuel")}
                unit={unitName}
                value={consumptionValue}
                handleChange={(e) => {
                  setConsumptionValue(e.target.value);
                  clearError("consumptionValue");
                }}
                error={errors.consumptionValue}
              />
            ) : (
              <CustomTextField
                required={
                  !(isWeightDistanceBased || isDistanceBased || isCat3x)
                }
                label={t("Consumption Value /Quantity")}
                unit={unitName}
                value={consumptionValue}
                handleChange={(e) => {
                  setConsumptionValue(e.target.value);
                  clearError("consumptionValue");
                }}
                error={errors.consumptionValue}
                disabled={
                  isWeightDistanceBased || isDistanceBased || isCat3x
                }
              />
            )}
          </Grid>

          {/* 月份重複錯誤提示 */}
          {isHelpText ? (
            <Grid size={12}>
              <Typography color="red">
                {t(
                  "Data already exists. Each month can only have one data entry.",
                )}
              </Typography>
            </Grid>
          ) : (
            ""
          )}
          {/* cat = 1_4：排放氣體資訊 */}
          {cat === "1_4" ? (
            <Grid size={12}>
              <Typography>
                {t("Emission Gas Type") + " : "}
                {emissionViewStore.selectedDataEntry.emissionFactorName || "-"}
              </Typography>
              {/* 計算方式:original filling amount */}
              {emissionViewStore.selectedDataEntry.calculationMethodId == 2 ? (
                <>
                  <Typography>
                    {t("Fugitive Emission Rate") + " : "}
                    {hasNumericValue(
                      emissionViewStore.selectedDataEntry.fugitiveEmissions,
                    )
                      ? `${emissionViewStore.selectedDataEntry.fugitiveEmissions}%`
                      : "-"}
                  </Typography>
                  <Typography>
                    {t("Calculated Value") + " : "}
                    {hasNumericValue(
                      emissionViewStore.selectedDataEntry.fugitiveEmissions,
                    )
                      ? (emissionViewStore.selectedDataEntry.fugitiveEmissions *
                          consumptionValue) / 100
                      : "-"}
                    {" " + emissionViewStore.selectedDataEntry.dataUnitName}
                  </Typography>
                </>
              ) : null}
              {/* 如果計算方式是質量平衡法 */}
              {emissionViewStore.selectedDataEntry.calculationMethodId == 3 ? (
                <>
                  <Typography>
                    {t("% by mass") + " : "}
                    {emissionViewStore.selectedDataEntry.value * 100 || "-"}
                    {/* //David說改為*100 */}
                  </Typography>
                  <Typography>
                    {t("Calculated Value") + " : "}
                    {emissionViewStore.selectedDataEntry.value
                      ? emissionViewStore.selectedDataEntry.value *
                        consumptionValue
                      : "-"}
                    {" " + emissionViewStore.selectedDataEntry.dataUnitName}
                  </Typography>
                </>
              ) : null}
            </Grid>
          ) : null}

          {/* 提示文字 */}
          <Grid size={12}>
            {Object.values(hints)
              .filter((item) => item && item.desc)
              .map((item, index) => (
                <Typography
                  key={index}
                  color="primary"
                  sx={{ paddingLeft: "8px" }}
                >
                  {t(item.desc)}
                </Typography>
              ))}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() =>
            emissionViewStore._handleSetIsShowDataEntryAddAndSetUnitModal(false)
          }
          variant="contained"
          sx={{ bgcolor: "#e0e0e0", color: "#000", textTransform: "none" }}
        >
          {t("Close")}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={isSubmitting || isDisabled}
          sx={{ textTransform: "none" }}
        >
          {isSubmitting ? t("Save...") : t("Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default DataEntryAddAndSetUnitModal;
